import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { updateTimetableSlot } from "../redux/actions/timetableSlotActions";
import { getTeachers } from "../redux/actions/teacherActions";
import { timetableSlotActions } from "../redux/slices/timetableSlot";
import { cleanFormData } from "../helpers/formHelpers";

import { TimetableSlotResponseDto } from "../shared/dtos/timetableSlot/TimetableSlotResponseDto";
import { TimetableSlotRequestDto } from "../shared/dtos/timetableSlot/TimetableSlotRequestDto";

// Explicit array mapping for Days of the Week
const dayOptions = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" },
];

const slotSchema = Yup.object().shape({
  teacherId: Yup.string().required("Teacher is required"),
  dayOfWeek: Yup.string().required("Day of week is required"),
  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string().required("End time is required"),
  location: Yup.string().required("Location is required"),
});

interface UpdateTimetableSlotFormProps {
  slot: TimetableSlotResponseDto;
  loading?: boolean;
  error?: string | null;
}

const UpdateTimetableSlotForm: React.FC<UpdateTimetableSlotFormProps> = ({
  slot,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: teachers } = useAppSelector((state) => state.teacher);

  useEffect(() => {
    if (!teachers || teachers.length === 0) {
      dispatch(getTeachers());
    }
  }, [dispatch, teachers]);

  const teacherOptions = useMemo(
    () =>
      teachers?.map((t) => ({
        value: t.id,
        label: t.name || "Unknown Teacher",
      })) || [],
    [teachers],
  );

  const initialValues: TimetableSlotRequestDto = {
    teacherId: slot?.teacherId || "",
    dayOfWeek: slot?.dayOfWeek || "MONDAY",
    location: slot?.location || "",
    // Input type="time" expects HH:mm
    startTime: slot?.startTime ? slot.startTime.substring(0, 5) : "",
    endTime: slot?.endTime ? slot.endTime.substring(0, 5) : "",
  };

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(timetableSlotActions.setErrorUpdatingTimetableSlot(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={slotSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          let startTime = rawCleaned.startTime;
          let endTime = rawCleaned.endTime;

          if (startTime && startTime.length === 5) {
            startTime += ":00";
          }
          if (endTime && endTime.length === 5) {
            endTime += ":00";
          }

          const cleanedValues: TimetableSlotRequestDto = {
            teacherId: rawCleaned.teacherId,
            dayOfWeek: rawCleaned.dayOfWeek,
            startTime,
            endTime,
            location: rawCleaned.location,
          };

          dispatch(updateTimetableSlot({ id: slot.id, values: cleanedValues }));
        }}
      >
        {({ dirty, isValid }: FormikProps<TimetableSlotRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1, textAlign: "left" }}
              >
                Updating schedule for{" "}
                <b>
                  {slot.subjectName} ({slot.subjectCode})
                </b>
                .
              </Typography>

              <FormSelectField
                name="teacherId"
                label="Assign Teacher"
                options={teacherOptions}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="dayOfWeek"
                  label="Day of the Week"
                  options={dayOptions}
                />
                <FormTextField
                  name="location"
                  label="Location / Room"
                  placeholder="e.g., Room 101"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormTextField
                  name="startTime"
                  label="Start Time"
                  type="time"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormTextField
                  name="endTime"
                  label="End Time"
                  type="time"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>

              <SubmitButton
                title="Save Changes"
                loading={loading}
                loadingText="Saving..."
                disabled={!dirty || !isValid || loading}
                startIcon={<SaveOutlinedIcon />}
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateTimetableSlotForm;
